/**
 * Mode dégradé (CP-12) — chercher dans le cache d'un poste, et dire son âge.
 *
 * Hors ligne, l'agent cherche comme en ligne : un nom, un matricule ou une
 * entreprise, sans égard à la casse ni aux accents. Et il lit TOUJOURS de
 * quand datent les données : un cache périmé peut laisser passer le salarié
 * d'une entreprise suspendue depuis — le risque est borné, il doit être
 * visible, pas silencieux.
 *
 * Pur : ni stockage, ni horloge.
 */
import { normaliserLibelle } from '../dns/synonymes.js';
import { dateNaissanceASaisir, eligibiliteAuJour } from './eligibilite.js';
export const RESULTATS_MAX_HORS_LIGNE = 50;
/**
 * Les personnes du cache qui répondent à la saisie : tous les mots tapés se
 * retrouvent dans le nom, le matricule, l'entreprise ou son code. Les noms qui
 * COMMENCENT par la saisie passent devant.
 */
export function chercherHorsLigne(personnes, saisie) {
    const mots = normaliserLibelle(saisie).split(' ').filter(Boolean);
    if (mots.length === 0 || mots.join('').length < 2)
        return [];
    const debut = mots.join(' ');
    const trouvees = [];
    for (const personne of personnes) {
        const nom = normaliserLibelle(personne.nomComplet);
        const botte = [
            nom,
            normaliserLibelle(personne.matricule ?? ''),
            normaliserLibelle(personne.entreprise ?? ''),
            normaliserLibelle(personne.entrepriseCode ?? ''),
        ].join(' ');
        if (mots.every((mot) => botte.includes(mot))) {
            trouvees.push({ personne, rang: nom.startsWith(debut) ? 0 : 1 });
        }
    }
    return trouvees
        .sort((a, b) => a.rang - b.rang || a.personne.nomComplet.localeCompare(b.personne.nomComplet, 'fr'))
        .slice(0, RESULTATS_MAX_HORS_LIGNE)
        .map((t) => t.personne);
}
export const SEUILS_FRAICHEUR_PAR_DEFAUT = {
    vieillieApresHeures: 24,
    perimeeApresHeures: 72,
};
export function fraicheurDuCache(genereLe, maintenant, seuils = SEUILS_FRAICHEUR_PAR_DEFAUT) {
    const heures = (maintenant.getTime() - genereLe.getTime()) / 3_600_000;
    if (heures > seuils.perimeeApresHeures)
        return 'PERIMEE';
    return heures > seuils.vieillieApresHeures ? 'VIEILLIE' : 'FRAICHE';
}
/** Un coffre plus vieux que cela ne s'ouvre plus, même sans réseau, et s'efface (S74, D5). */
export const AGE_MAX_COFFRE_JOURS = 7;
export function coffreTropVieux(scelleLe, maintenant) {
    return maintenant.getTime() - scelleLe.getTime() > AGE_MAX_COFFRE_JOURS * 24 * 3_600_000;
}
/**
 * Un coffre ouvert est-il passé hors délai (S77, S3) ? `scelleLe` est en clair
 * sur le disque, donc modifiable : on lit aussi `genereLe`, qui dort chiffré
 * dans le coffre. À relire tant que l'écran reste ouvert.
 */
export function coffreExpire(dates, maintenant) {
    return (coffreTropVieux(new Date(dates.scelleLe), maintenant) ||
        coffreTropVieux(new Date(dates.genereLe), maintenant));
}
export const MESSAGE_ATTENTE_HORS_LIGNE = {
    ECART_OFFRE: 'Écart d’offre en attente : entrée impossible tant que la chargée n’a pas tranché. ' +
        'Notez l’offre lue sur la carte.',
    DATE_NAISSANCE: 'Date de naissance à saisir : notez-la sur le formulaire papier.',
};
/**
 * Le verdict d'une personne du coffre au jour `aujourdHui` (AAAA-MM-JJ, date
 * civile de Madagascar) : recalculé avec la règle d'en ligne à partir des
 * conditions gardées (S74, D2), et non figé à la synchronisation. Sans
 * conditions — client externe, fiche sans contrat, coffre d'avant — c'est le
 * verdict de la synchronisation.
 */
export function verdictHorsLigne(personne, aujourdHui) {
    const attentes = [];
    if (personne.ecartOffre === true)
        attentes.push('ECART_OFFRE');
    if (dateNaissanceASaisir({ typeBeneficiaire: personne.type, dateNaissance: personne.dateNaissance }))
        attentes.push('DATE_NAISSANCE');
    if (personne.conditions === null || personne.conditions === undefined) {
        return { eligible: personne.eligible, motif: personne.motif, attentes };
    }
    const resultat = eligibiliteAuJour(personne.conditions, { typeBeneficiaire: personne.type, dateNaissance: personne.dateNaissance }, aujourdHui);
    return resultat.eligible
        ? { eligible: true, motif: null, attentes }
        : { eligible: false, motif: resultat.motif, attentes };
}
//# sourceMappingURL=hors-ligne.js.map